import { useState } from 'react';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import {
  TableBox, IncludedBox, OrderStatusDot, StyledIconButton, StyledNavigateButton
} from '../../OrderHistory.styles';
import { getDerivedStatus, correctDisplaySource } from '../../utils';
import { orderRow } from '../../utils/proptypes';

export const PurchasedRow = ({
  isPlan, row, displayName, date, open, setOpen
}) => {
  const navigate = useNavigate();
  const { businessEntityUuid } = useParams();

  const trackNavigateToOrder = useButtonPressedCallback({
    elementName: 'navigate_to_order_button',
    page: 'order_history'
  });

  const trackExpandCollapse = useButtonPressedCallback({
    elementName: 'expand_collapse_order_button',
    page: 'order_history'
  });

  const handleOrderClick = (e) => {
    trackNavigateToOrder(e, {
      orderTaskUuid: row.orderTaskUuid,
      orderTaskType: row.orderTaskType
    });
    navigate(`/dashboard/${businessEntityUuid}/subscription-hub-v2/orders/${row.orderTaskUuid}`);
  };

  const handleExpandClick = (e) => {
    trackExpandCollapse(e, {
      action: open ? 'collapse' : 'expand',
      orderTaskType: row.orderTaskType
    });
    setOpen(!open);
  };

  return (
    <>
      <td>
        <TableBox>
          <Box display="flex">
            <Text data-testid="display-name" color="primary" size="md">{displayName}</Text>
            {isPlan && (
              <StyledIconButton
                data-testid="expand-row"
                label="expand row"
                size="small"
                onClick={handleExpandClick}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </StyledIconButton>

            )}
          </Box>
        </TableBox>
        {isPlan && open && (
          row.included.map((item) => (
            <IncludedBox sx={{ maxWidth: '275px' }} key={item.orderTaskType}>
              <Text size="sm" color="secondary">{item.displayName}</Text>
            </IncludedBox>
          ))
        )}
      </td>
      <td>
        <TableBox>
          <Box display="flex">
            {!row.isTransactional && (
            <Text data-testid="fulfillment-status" size="md" color="primary" sx={{ textTransform: 'capitalize', paddingTop: '3px' }}>

              <>
                {isPlan && 'Plan'}
                {' '}
                {row.orderTaskStatus}
              </>

            </Text>
            )}
          </Box>
        </TableBox>
        <Box sx={{ ...(row.isTransactional && { paddingTop: '24px' }) }}>
          {isPlan && open && (
            row.included.map((item) => {
              const status = getDerivedStatus(item.orderTaskStatus);
              return (
                <IncludedBox
                  key={item.orderTaskType}
                  sx={{
                    padding: '5px 0 5px 18px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <OrderStatusDot status={status} />
                  <Text size="sm" color="secondary" sx={{ textTransform: 'capitalize' }}>{status}</Text>
                </IncludedBox>
              );
            })
          )}
        </Box>
      </td>
      <td>
        <TableBox>
          <Box display="flex" width="100%">
            <Text data-testid="order-date" size="md" color="primary" sx={{ flex: 1, paddingTop: '3px' }}>{date}</Text>
            <Box flex={0} paddingRight="2rem">
              <StyledNavigateButton size="small" label="navigate to order" data-testid="navigate-to-order" onClick={handleOrderClick}>
                <ArrowForwardIosIcon />
              </StyledNavigateButton>
            </Box>
          </Box>
        </TableBox>
      </td>
    </>
  );
};

PurchasedRow.propTypes = {
  row: orderRow,
  isPlan: PropTypes.bool.isRequired,
  displayName: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
};

export default function OrderRow({ row, isPlan }) {
  const [open, setOpen] = useState(false);
  const { date, displayName } = correctDisplaySource(row);

  return (
    <tr>
      <PurchasedRow
        open={open}
        setOpen={setOpen}
        isPlan={isPlan}
        row={row}
        date={date}
        displayName={displayName}
      />
    </tr>
  );
}

OrderRow.propTypes = {
  row: orderRow.isRequired,
  isPlan: PropTypes.bool.isRequired
};
